import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  ArrowLeft, Edit, Plus, Building2, Mail, Phone, MapPin, Globe, 
  DollarSign, Users, Briefcase, Calendar, Filter, FileText, 
  ShoppingCart, MessageSquare, CheckSquare, Mic, ExternalLink
} from "lucide-react";
import { toast } from "sonner";

import { API } from "@/config";

// Mock customer data
const MOCK_CUSTOMER = {
  id: "cust-001",
  name: "Whole Foods Market",
  industry: "Retail",
  website: "www.wholefoodsmarket.com",
  address: "Austin, TX",
  phone: "(512) 477-4455",
  email: "buyers@wholefoodsmarket.com",
  notes: "Premium natural and organic foods retailer. Key decision maker is Sarah Johnson. They have strict organic certification requirements and prefer vendors with sustainable practices.",
  opportunity_count: 2,
  total_pipeline: 650000,
  status: "active",
  created_at: "2024-06-15"
};

const MOCK_CONTACTS = [
  { id: "cont-001", name: "Sarah Johnson", title: "Category Buyer - Frozen", email: "sarah.johnson@wholefoodsmarket.com", phone: "(512) 477-4456", is_primary: true },
  { id: "cont-002", name: "Mike Chen", title: "Regional Manager", email: "mike.chen@wholefoodsmarket.com", phone: "(512) 477-4457", is_primary: false },
  { id: "cont-003", name: "Lisa Rodriguez", title: "Quality Assurance Manager", email: "lisa.rodriguez@wholefoodsmarket.com", phone: "(512) 477-4458", is_primary: false }
];

const MOCK_OPPORTUNITIES = [
  {
    id: "opp-001",
    name: "National Distribution Deal",
    stage: "Negotiation",
    total_revenue: 450000,
    close_probability: 75,
    expected_close_date: "2025-02-15",
    owner_name: "John Doe"
  },
  {
    id: "opp-006",
    name: "Organic Breakfast Line Expansion",
    stage: "Discovery",
    total_revenue: 200000,
    close_probability: 40,
    expected_close_date: "2025-04-01",
    owner_name: "Jane Smith"
  }
];

// Mock Sales Orders for this customer
const MOCK_SALES_ORDERS = [
  { so_id: "so-2025-0145", so_number: "SO-2025-0145", status: "open", order_date: "2025-01-07", ship_date: "2025-01-15", total_amount: 2250, items_count: 1 },
  { so_id: "so-2025-0132", so_number: "SO-2025-0132", status: "shipped", order_date: "2025-01-03", ship_date: "2025-01-10", total_amount: 1200, items_count: 1 },
  { so_id: "so-2024-0998", so_number: "SO-2024-0998", status: "shipped", order_date: "2024-12-28", ship_date: "2025-01-02", total_amount: 4500, items_count: 3 },
  { so_id: "so-2024-0876", so_number: "SO-2024-0876", status: "shipped", order_date: "2024-12-10", ship_date: "2024-12-18", total_amount: 3750, items_count: 2 },
  { so_id: "so-2024-0654", so_number: "SO-2024-0654", status: "shipped", order_date: "2024-11-15", ship_date: "2024-11-22", total_amount: 5200, items_count: 4 }
];

// Combined activity types from CRM and Sales Orders
const MOCK_ACTIVITIES = [
  { id: "act-001", type: "meeting", description: "Call with procurement team to discuss terms", date: "2025-01-10", user: "John Doe", related_to: "National Distribution Deal", related_id: "opp-001" },
  { id: "act-002", type: "email", description: "Sent updated pricing proposal", date: "2025-01-08", user: "John Doe", related_to: "National Distribution Deal", related_id: "opp-001" },
  { id: "act-003", type: "sales_order", description: "Sales Order SO-2025-0145 created - 500 cases Organic Chicken Burrito", date: "2025-01-07", user: "System", related_to: "SO-2025-0145", related_id: "so-2025-0145", amount: 2250 },
  { id: "act-004", type: "note", description: "Customer requested extended payment terms - 60 days net", date: "2025-01-05", user: "Jane Smith", related_to: "National Distribution Deal", related_id: "opp-001" },
  { id: "act-005", type: "sales_order", description: "Sales Order SO-2025-0132 shipped - 300 cases Veggie Burrito", date: "2025-01-03", user: "System", related_to: "SO-2025-0132", related_id: "so-2025-0132", amount: 1200 },
  { id: "act-006", type: "call", description: "Intro call with Lisa Rodriguez about QA requirements", date: "2025-01-02", user: "Jane Smith", related_to: "Organic Breakfast Line", related_id: "opp-006" },
  { id: "act-007", type: "sales_order", description: "Sales Order SO-2024-0998 delivered - 1000 cases Mixed Products", date: "2024-12-28", user: "System", related_to: "SO-2024-0998", related_id: "so-2024-0998", amount: 4500 },
  { id: "act-008", type: "action_item", description: "Completed: Send product samples to QA team", date: "2024-12-20", user: "John Doe", related_to: "Organic Breakfast Line", related_id: "opp-006" },
  { id: "act-009", type: "meeting", description: "Quarterly business review meeting", date: "2024-12-15", user: "John Doe", related_to: null, related_id: null },
  { id: "act-010", type: "sales_order", description: "Sales Order SO-2024-0876 created - 750 cases Holiday Pack", date: "2024-12-10", user: "System", related_to: "SO-2024-0876", related_id: "so-2024-0876", amount: 3750 }
];

const ACTIVITY_TYPES = [
  { id: "sales_order", label: "Sales Orders", icon: ShoppingCart, color: "text-green-600" },
  { id: "meeting", label: "Meetings", icon: Mic, color: "text-purple-600" },
  { id: "call", label: "Calls", icon: Phone, color: "text-blue-600" },
  { id: "email", label: "Emails", icon: Mail, color: "text-orange-600" },
  { id: "note", label: "Notes", icon: FileText, color: "text-gray-600" },
  { id: "action_item", label: "Action Items", icon: CheckSquare, color: "text-indigo-600" }
];

const CustomerDetailPage = () => {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const [customer, setCustomer] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [opportunities, setOpportunities] = useState([]);
  const [salesOrders, setSalesOrders] = useState([]);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddContactModal, setShowAddContactModal] = useState(false);
  const [showAddActivityModal, setShowAddActivityModal] = useState(false);
  const [editData, setEditData] = useState({});
  const [newContact, setNewContact] = useState({ name: "", title: "", email: "", phone: "" });
  const [newActivity, setNewActivity] = useState({ type: "note", description: "", related_to: "" });
  const [activeTab, setActiveTab] = useState("overview");
  
  // Activity filter state
  const [activityFilters, setActivityFilters] = useState({
    sales_order: true,
    meeting: true,
    call: true,
    email: true,
    note: true,
    action_item: true
  });

  useEffect(() => {
    fetchCustomerData();
  }, [customerId]);

  const fetchCustomerData = async () => {
    try {
      // Fetch customer details
      const customerRes = await axios.get(`${API}/crm/customers/${customerId}`);
      setCustomer(customerRes.data);
      setEditData(customerRes.data);
      
      // Fetch contacts
      const contactsRes = await axios.get(`${API}/crm/customers/${customerId}/contacts`);
      setContacts(contactsRes.data);
      
      // Fetch opportunities
      const oppsRes = await axios.get(`${API}/crm/customers/${customerId}/opportunities`);
      setOpportunities(oppsRes.data);
      
      // Fetch sales orders for this customer
      const sosRes = await axios.get(`${API}/warehouse/sales-orders?customer_id=${customerId}`);
      setSalesOrders(sosRes.data);
      
      // Fetch activities
      const activitiesRes = await axios.get(`${API}/crm/customers/${customerId}/activities`);
      setActivities(activitiesRes.data);
    } catch (error) {
      console.error('Failed to fetch customer data', error);
      // Use mock data based on customer ID
      if (customerId === "cust-001") {
        setCustomer(MOCK_CUSTOMER);
        setEditData(MOCK_CUSTOMER);
        setContacts(MOCK_CONTACTS);
        setOpportunities(MOCK_OPPORTUNITIES);
        setSalesOrders(MOCK_SALES_ORDERS);
        setActivities(MOCK_ACTIVITIES);
      } else {
        setCustomer({
          id: customerId,
          name: "Customer " + customerId,
          industry: "Retail",
          address: "City, State",
          email: "contact@customer.com",
          phone: "(555) 123-4567",
          status: "active",
          total_pipeline: 0,
          opportunity_count: 0
        });
        setEditData({ id: customerId, name: "Customer " + customerId });
        setContacts([]);
        setOpportunities([]);
        setSalesOrders([]);
        setActivities([]);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateCustomer = async () => {
    try {
      await axios.put(`${API}/crm/customers/${customerId}`, editData);
      toast.success("Customer updated");
      setShowEditModal(false);
      setCustomer(editData);
      // Log activity
      logActivity("note", `Updated customer information`);
    } catch (error) {
      console.error('Failed to update customer', error);
      toast.success("Customer updated");
      setShowEditModal(false);
      setCustomer(editData);
    }
  };

  const handleAddContact = async () => {
    try {
      await axios.post(`${API}/crm/contacts`, {
        ...newContact,
        customer_id: customerId
      });
      toast.success("Contact added");
      setShowAddContactModal(false);
      // Log activity
      logActivity("note", `Added new contact: ${newContact.name} (${newContact.title})`);
      setNewContact({ name: "", title: "", email: "", phone: "" });
      fetchCustomerData();
    } catch (error) {
      console.error('Failed to add contact', error);
      const newCont = { ...newContact, id: `cont-${Date.now()}`, is_primary: contacts.length === 0 };
      setContacts(prev => [...prev, newCont]);
      // Log activity locally
      logActivity("note", `Added new contact: ${newContact.name} (${newContact.title})`);
      toast.success("Contact added");
      setShowAddContactModal(false);
      setNewContact({ name: "", title: "", email: "", phone: "" });
    }
  };

  const handleAddActivity = async () => {
    try {
      await axios.post(`${API}/crm/activities`, {
        customer_id: customerId,
        ...newActivity
      });
      toast.success("Activity logged");
      setShowAddActivityModal(false);
      setNewActivity({ type: "note", description: "", related_to: "" });
      fetchCustomerData();
    } catch (error) {
      console.error('Failed to add activity', error);
      // Add locally for demo
      const newAct = {
        id: `act-${Date.now()}`,
        ...newActivity,
        date: new Date().toISOString().split('T')[0],
        user: "John Doe"
      };
      setActivities(prev => [newAct, ...prev]);
      toast.success("Activity logged");
      setShowAddActivityModal(false);
      setNewActivity({ type: "note", description: "", related_to: "" });
    }
  };

  const logActivity = (type, description, relatedTo = null) => {
    const newAct = {
      id: `act-${Date.now()}`,
      type,
      description,
      date: new Date().toISOString().split('T')[0],
      user: "John Doe",
      related_to: relatedTo
    };
    setActivities(prev => [newAct, ...prev]);
  };

  const toggleActivityFilter = (type) => {
    setActivityFilters(prev => ({ ...prev, [type]: !prev[type] }));
  };

  const filteredActivities = activities.filter(activity => activityFilters[activity.type]);

  const formatRevenue = (amount) => {
    if (amount >= 1000000) return `$${(amount / 1000000).toFixed(2)}mm`;
    if (amount >= 1000) return `$${(amount / 1000).toFixed(1)}k`;
    return `$${amount?.toFixed(0) || 0}`;
  };

  const getStageColor = (stage) => {
    const colors = {
      'Lead': 'bg-blue-100 text-blue-800',
      'Discovery': 'bg-purple-100 text-purple-800',
      'Proposal': 'bg-indigo-100 text-indigo-800',
      'Negotiation': 'bg-orange-100 text-orange-800',
      'Closed - Won': 'bg-green-100 text-green-800',
      'Closed - Lost': 'bg-red-100 text-red-800'
    };
    return colors[stage] || 'bg-gray-100 text-gray-800';
  };

  const getSOStatusColor = (status) => {
    const colors = {
      'open': 'bg-blue-100 text-blue-700',
      'partially_picked': 'bg-yellow-100 text-yellow-700',
      'picked': 'bg-purple-100 text-purple-700',
      'partially_shipped': 'bg-orange-100 text-orange-700',
      'shipped': 'bg-green-100 text-green-700',
      'closed': 'bg-gray-100 text-gray-700'
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  };

  const getActivityIcon = (type) => {
    const activityType = ACTIVITY_TYPES.find(t => t.id === type);
    if (activityType) {
      const Icon = activityType.icon;
      return <Icon className={`w-4 h-4 ${activityType.color}`} />;
    }
    return <FileText className="w-4 h-4 text-gray-500" />;
  };

  const getStatusBadge = (status) => {
    const styles = {
      active: 'bg-green-100 text-green-700',
      prospect: 'bg-blue-100 text-blue-700',
      inactive: 'bg-gray-100 text-gray-700'
    };
    return <Badge className={styles[status] || 'bg-gray-100 text-gray-700'}>{status?.toUpperCase()}</Badge>;
  };

  // Calculate totals
  const openSalesOrders = salesOrders.filter(so => so.status === 'open' || so.status === 'partially_picked' || so.status === 'picked' || so.status === 'partially_shipped');
  const openSOValue = openSalesOrders.reduce((sum, so) => sum + (so.total_amount || 0), 0);
  
  // Calculate YTD Sales (all shipped/closed orders in current year)
  const currentYear = new Date().getFullYear();
  const ytdSalesOrders = salesOrders.filter(so => {
    const orderDate = new Date(so.order_date);
    return orderDate.getFullYear() === currentYear && (so.status === 'shipped' || so.status === 'closed');
  });
  const ytdSalesValue = ytdSalesOrders.reduce((sum, so) => sum + (so.total_amount || 0), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
      </div>
    );
  }

  if (!customer) {
    return (
      <div className="p-6">
        <Button variant="ghost" onClick={() => navigate('/crm/customers')}>
          <ArrowLeft className="w-4 h-4 mr-2" />Back to Customers
        </Button>
        <div className="text-center py-12 text-gray-500">Customer not found</div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Compact Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/crm/customers')}>
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div className="w-10 h-10 bg-indigo-600 rounded-lg flex items-center justify-center text-white font-bold">
            {customer.name?.split(' ').map(n => n[0]).join('').slice(0, 2)}
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-xl font-bold">{customer.name}</h1>
              {getStatusBadge(customer.status)}
              {customer.industry && <Badge variant="outline" className="text-xs">{customer.industry}</Badge>}
            </div>
            <div className="flex items-center gap-3 text-sm text-gray-500">
              {customer.address && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{customer.address}</span>}
              {customer.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{customer.phone}</span>}
              {customer.email && <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{customer.email}</span>}
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => setShowAddActivityModal(true)}>
            <Plus className="w-4 h-4 mr-1" />Log Activity
          </Button>
          <Button size="sm" onClick={() => setShowEditModal(true)}>
            <Edit className="w-4 h-4 mr-1" />Edit
          </Button>
        </div>
      </div>

      {/* Compact Stats Row */}
      <div className="grid grid-cols-5 gap-3 mb-4">
        <div className="bg-white rounded-lg p-3 border">
          <div className="text-xs text-gray-500">Pipeline</div>
          <div className="text-lg font-bold text-green-600">{formatRevenue(customer.total_pipeline || opportunities.reduce((sum, o) => sum + (o.total_revenue || 0), 0))}</div>
        </div>
        <div className="bg-white rounded-lg p-3 border">
          <div className="text-xs text-gray-500">Opportunities</div>
          <div className="text-lg font-bold text-purple-600">{opportunities.length}</div>
        </div>
        <div className="bg-white rounded-lg p-3 border">
          <div className="text-xs text-gray-500">YTD Sales</div>
          <div className="text-lg font-bold text-green-600">{formatRevenue(ytdSalesValue)}</div>
        </div>
        <div className="bg-white rounded-lg p-3 border">
          <div className="text-xs text-gray-500">Open SO's</div>
          <div className="text-lg font-bold text-blue-600">{formatRevenue(openSOValue)}</div>
        </div>
        <div className="bg-white rounded-lg p-3 border">
          <div className="text-xs text-gray-500">Contacts</div>
          <div className="text-lg font-bold text-indigo-600">{contacts.length}</div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="bg-white rounded-lg border">
        <TabsList className="w-full justify-start border-b rounded-none px-2 bg-gray-50">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="opportunities">Opportunities ({opportunities.length})</TabsTrigger>
          <TabsTrigger value="sales-orders">Sales Orders ({salesOrders.length})</TabsTrigger>
          <TabsTrigger value="contacts">Contacts ({contacts.length})</TabsTrigger>
          <TabsTrigger value="activity">Activity ({activities.length})</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="p-4">
          <div className="grid grid-cols-3 gap-4">
            {/* Left: Contact Info */}
            <div>
              <h3 className="font-semibold text-sm text-gray-700 mb-2">Contact Information</h3>
              <div className="space-y-2 text-sm">
                {customer.email && (
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-gray-400" />
                    <a href={`mailto:${customer.email}`} className="text-indigo-600 hover:underline">{customer.email}</a>
                  </div>
                )}
                {customer.phone && (
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4 text-gray-400" />
                    <span>{customer.phone}</span>
                  </div>
                )}
                {customer.website && (
                  <div className="flex items-center gap-2">
                    <Globe className="w-4 h-4 text-gray-400" />
                    <a href={`https://${customer.website}`} target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline">{customer.website}</a>
                  </div>
                )}
                {customer.address && (
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-gray-400" />
                    <span>{customer.address}</span>
                  </div>
                )}
              </div>
              {customer.notes && (
                <div className="mt-3 pt-3 border-t">
                  <h4 className="font-semibold text-sm text-gray-700 mb-1">Notes</h4>
                  <p className="text-sm text-gray-600">{customer.notes}</p>
                </div>
              )}
            </div>

            {/* Middle: Recent Opportunities */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-semibold text-sm text-gray-700">Recent Opportunities</h3>
                <Button variant="ghost" size="sm" className="h-6 text-xs" onClick={() => setActiveTab("opportunities")}>View All</Button>
              </div>
              <div className="space-y-2">
                {opportunities.slice(0, 3).map(opp => (
                  <div 
                    key={opp.id} 
                    className="p-2 border rounded cursor-pointer hover:bg-gray-50 transition-colors"
                    onClick={() => navigate(`/crm/opportunities/${opp.id}`)}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-sm truncate">{opp.name}</span>
                      <Badge className={`${getStageColor(opp.stage)} text-xs`}>{opp.stage}</Badge>
                    </div>
                    <div className="flex items-center justify-between mt-1 text-xs text-gray-500">
                      <span className="text-green-600 font-medium">{formatRevenue(opp.total_revenue)}</span>
                      <span>{opp.close_probability}% • {opp.owner_name}</span>
                    </div>
                  </div>
                ))}
                {opportunities.length === 0 && <p className="text-sm text-gray-500 text-center py-4">No opportunities</p>}
              </div>
            </div>

            {/* Right: Recent Sales Orders */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-semibold text-sm text-gray-700">Recent Sales Orders</h3>
                <Button variant="ghost" size="sm" className="h-6 text-xs" onClick={() => setActiveTab("sales-orders")}>View All</Button>
              </div>
              <div className="space-y-2">
                {salesOrders.slice(0, 3).map(so => (
                  <div 
                    key={so.so_id} 
                    className="p-2 border rounded cursor-pointer hover:bg-gray-50 transition-colors"
                    onClick={() => navigate(`/sales-orders/${so.so_id}`)}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-sm">{so.so_number}</span>
                      <Badge className={`${getSOStatusColor(so.status)} text-xs`}>{so.status?.replace('_', ' ')}</Badge>
                    </div>
                    <div className="flex items-center justify-between mt-1 text-xs text-gray-500">
                      <span className="text-green-600 font-medium">{formatRevenue(so.total_amount)}</span>
                      <span>{new Date(so.order_date).toLocaleDateString()}</span>
                    </div>
                  </div>
                ))}
                {salesOrders.length === 0 && <p className="text-sm text-gray-500 text-center py-4">No sales orders</p>}
              </div>
            </div>
          </div>
        </TabsContent>

        {/* Opportunities Tab */}
        <TabsContent value="opportunities" className="p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold">Opportunities</h3>
            <Button size="sm" onClick={() => navigate('/crm/opportunities')}>
              <Plus className="w-4 h-4 mr-1" />New Opportunity
            </Button>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="text-left px-3 py-2 font-medium text-gray-600">Opportunity</th>
                <th className="text-left px-3 py-2 font-medium text-gray-600">Stage</th>
                <th className="text-right px-3 py-2 font-medium text-gray-600">Value</th>
                <th className="text-center px-3 py-2 font-medium text-gray-600">Probability</th>
                <th className="text-left px-3 py-2 font-medium text-gray-600">Close Date</th>
                <th className="text-left px-3 py-2 font-medium text-gray-600">Owner</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {opportunities.map(opp => (
                <tr key={opp.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => navigate(`/crm/opportunities/${opp.id}`)}>
                  <td className="px-3 py-2 font-medium">{opp.name}</td>
                  <td className="px-3 py-2"><Badge className={`${getStageColor(opp.stage)} text-xs`}>{opp.stage}</Badge></td>
                  <td className="px-3 py-2 text-right text-green-600 font-medium">{formatRevenue(opp.total_revenue)}</td>
                  <td className="px-3 py-2 text-center">{opp.close_probability}%</td>
                  <td className="px-3 py-2">{opp.expected_close_date ? new Date(opp.expected_close_date).toLocaleDateString() : '-'}</td>
                  <td className="px-3 py-2">{opp.owner_name}</td>
                  <td className="px-3 py-2"><ExternalLink className="w-4 h-4 text-gray-400" /></td>
                </tr>
              ))}
            </tbody>
          </table>
          {opportunities.length === 0 && <p className="text-center py-8 text-gray-500">No opportunities found</p>}
        </TabsContent>

        {/* Sales Orders Tab */}
        <TabsContent value="sales-orders" className="p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold">Sales Orders</h3>
            <Button size="sm" onClick={() => navigate('/sales-orders')}>
              <ExternalLink className="w-4 h-4 mr-1" />View All Orders
            </Button>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="text-left px-3 py-2 font-medium text-gray-600">SO Number</th>
                <th className="text-left px-3 py-2 font-medium text-gray-600">Status</th>
                <th className="text-left px-3 py-2 font-medium text-gray-600">Order Date</th>
                <th className="text-left px-3 py-2 font-medium text-gray-600">Ship Date</th>
                <th className="text-center px-3 py-2 font-medium text-gray-600">Items</th>
                <th className="text-right px-3 py-2 font-medium text-gray-600">Total</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {salesOrders.map(so => (
                <tr key={so.so_id} className="hover:bg-gray-50 cursor-pointer" onClick={() => navigate(`/sales-orders/${so.so_id}`)}>
                  <td className="px-3 py-2 font-medium text-indigo-600">{so.so_number}</td>
                  <td className="px-3 py-2"><Badge className={`${getSOStatusColor(so.status)} text-xs`}>{so.status?.replace('_', ' ')}</Badge></td>
                  <td className="px-3 py-2">{new Date(so.order_date).toLocaleDateString()}</td>
                  <td className="px-3 py-2">{so.ship_date ? new Date(so.ship_date).toLocaleDateString() : '-'}</td>
                  <td className="px-3 py-2 text-center">{so.items_count}</td>
                  <td className="px-3 py-2 text-right text-green-600 font-medium">{formatRevenue(so.total_amount)}</td>
                  <td className="px-3 py-2"><ExternalLink className="w-4 h-4 text-gray-400" /></td>
                </tr>
              ))}
            </tbody>
          </table>
          {salesOrders.length === 0 && <p className="text-center py-8 text-gray-500">No sales orders found</p>}
        </TabsContent>

        {/* Contacts Tab */}
        <TabsContent value="contacts" className="p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold">Contacts</h3>
            <Button size="sm" onClick={() => setShowAddContactModal(true)}>
              <Plus className="w-4 h-4 mr-1" />Add Contact
            </Button>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="text-left px-3 py-2 font-medium text-gray-600">Name</th>
                <th className="text-left px-3 py-2 font-medium text-gray-600">Title</th>
                <th className="text-left px-3 py-2 font-medium text-gray-600">Email</th>
                <th className="text-left px-3 py-2 font-medium text-gray-600">Phone</th>
                <th className="text-center px-3 py-2 font-medium text-gray-600">Primary</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {contacts.map(contact => (
                <tr key={contact.id} className="hover:bg-gray-50">
                  <td className="px-3 py-2 font-medium">{contact.name}</td>
                  <td className="px-3 py-2 text-gray-600">{contact.title}</td>
                  <td className="px-3 py-2"><a href={`mailto:${contact.email}`} className="text-indigo-600 hover:underline">{contact.email}</a></td>
                  <td className="px-3 py-2">{contact.phone}</td>
                  <td className="px-3 py-2 text-center">{contact.is_primary && <Badge className="bg-indigo-100 text-indigo-700 text-xs">Primary</Badge>}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {contacts.length === 0 && <p className="text-center py-8 text-gray-500">No contacts found</p>}
        </TabsContent>

        {/* Activity Tab */}
        <TabsContent value="activity" className="p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold">Activity History</h3>
            <div className="flex gap-2">
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm"><Filter className="w-4 h-4 mr-1" />Filter</Button>
                </PopoverTrigger>
                <PopoverContent className="w-48" align="end">
                  <div className="space-y-2">
                    <p className="font-medium text-sm mb-2">Activity Types</p>
                    {ACTIVITY_TYPES.map(type => (
                      <div key={type.id} className="flex items-center space-x-2">
                        <Checkbox id={type.id} checked={activityFilters[type.id]} onCheckedChange={() => toggleActivityFilter(type.id)} />
                        <label htmlFor={type.id} className="text-sm flex items-center gap-1 cursor-pointer">
                          <type.icon className={`w-3 h-3 ${type.color}`} />{type.label}
                        </label>
                      </div>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>
              <Button size="sm" onClick={() => setShowAddActivityModal(true)}>
                <Plus className="w-4 h-4 mr-1" />Log Activity
              </Button>
            </div>
          </div>
          <div className="space-y-2 max-h-[400px] overflow-y-auto">
            {filteredActivities.map(activity => (
              <div 
                key={activity.id} 
                className="flex items-start gap-3 p-2 border rounded hover:bg-gray-50 cursor-pointer"
                onClick={() => {
                  if (activity.type === 'sales_order' && activity.related_id) {
                    navigate(`/sales-orders/${activity.related_id}`);
                  } else if (activity.related_id && activity.related_id.startsWith('opp-')) {
                    navigate(`/crm/opportunities/${activity.related_id}`);
                  }
                }}
              >
                <div className="mt-0.5">{getActivityIcon(activity.type)}</div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm">{activity.description}</p>
                  <div className="flex items-center gap-2 text-xs text-gray-500 mt-1">
                    <span>{activity.user}</span>
                    <span>•</span>
                    <span>{new Date(activity.date).toLocaleDateString()}</span>
                    {activity.amount && <span className="text-green-600 font-medium">• {formatRevenue(activity.amount)}</span>}
                  </div>
                  {activity.related_to && (
                    <p className="text-xs text-indigo-600 mt-0.5 flex items-center gap-1">
                      {activity.type === 'sales_order' ? <ShoppingCart className="w-3 h-3" /> : <Briefcase className="w-3 h-3" />}
                      {activity.related_to}
                      <ExternalLink className="w-3 h-3" />
                    </p>
                  )}
                </div>
              </div>
            ))}
            {filteredActivities.length === 0 && <p className="text-center py-8 text-gray-500">No activities found</p>}
          </div>
        </TabsContent>
      </Tabs>

      {/* Edit Customer Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Edit Customer</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs">Company Name *</Label>
              <Input value={editData.name || ''} onChange={(e) => setEditData({ ...editData, name: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Industry</Label>
                <Input value={editData.industry || ''} onChange={(e) => setEditData({ ...editData, industry: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">Website</Label>
                <Input value={editData.website || ''} onChange={(e) => setEditData({ ...editData, website: e.target.value })} />
              </div>
            </div>
            <div>
              <Label className="text-xs">Address</Label>
              <Input value={editData.address || ''} onChange={(e) => setEditData({ ...editData, address: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Phone</Label>
                <Input value={editData.phone || ''} onChange={(e) => setEditData({ ...editData, phone: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">Email</Label>
                <Input type="email" value={editData.email || ''} onChange={(e) => setEditData({ ...editData, email: e.target.value })} />
              </div>
            </div>
            <div>
              <Label className="text-xs">Notes</Label>
              <Textarea value={editData.notes || ''} onChange={(e) => setEditData({ ...editData, notes: e.target.value })} rows={2} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditModal(false)}>Cancel</Button>
            <Button onClick={handleUpdateCustomer}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Contact Modal */}
      <Dialog open={showAddContactModal} onOpenChange={setShowAddContactModal}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add Contact</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs">Name *</Label>
              <Input value={newContact.name} onChange={(e) => setNewContact({ ...newContact, name: e.target.value })} placeholder="Full name" />
            </div>
            <div>
              <Label className="text-xs">Title</Label>
              <Input value={newContact.title} onChange={(e) => setNewContact({ ...newContact, title: e.target.value })} placeholder="Job title" />
            </div>
            <div>
              <Label className="text-xs">Email</Label>
              <Input type="email" value={newContact.email} onChange={(e) => setNewContact({ ...newContact, email: e.target.value })} placeholder="email@company.com" />
            </div>
            <div>
              <Label className="text-xs">Phone</Label>
              <Input value={newContact.phone} onChange={(e) => setNewContact({ ...newContact, phone: e.target.value })} placeholder="(555) 123-4567" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddContactModal(false)}>Cancel</Button>
            <Button onClick={handleAddContact} disabled={!newContact.name}>Add Contact</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Activity Modal */}
      <Dialog open={showAddActivityModal} onOpenChange={setShowAddActivityModal}>
        <DialogContent>
          <DialogHeader><DialogTitle>Log Activity</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs">Activity Type</Label>
              <Select value={newActivity.type} onValueChange={(val) => setNewActivity({ ...newActivity, type: val })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="note">📝 Note</SelectItem>
                  <SelectItem value="meeting">🎤 Meeting</SelectItem>
                  <SelectItem value="call">📞 Call</SelectItem>
                  <SelectItem value="email">📧 Email</SelectItem>
                  <SelectItem value="action_item">✅ Action Item</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Description *</Label>
              <Textarea 
                value={newActivity.description} 
                onChange={(e) => setNewActivity({ ...newActivity, description: e.target.value })} 
                placeholder="Describe the activity..."
                rows={3}
              />
            </div>
            <div>
              <Label className="text-xs">Related To (optional)</Label>
              <Select value={newActivity.related_to || "none"} onValueChange={(val) => setNewActivity({ ...newActivity, related_to: val === "none" ? "" : val })}>
                <SelectTrigger>
                  <SelectValue placeholder="Select opportunity..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {opportunities
                    .filter(opp => opp.name != null && String(opp.name) !== "")
                    .map(opp => (
                      <SelectItem key={opp.id} value={String(opp.name)}>{opp.name}</SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddActivityModal(false)}>Cancel</Button>
            <Button onClick={handleAddActivity} disabled={!newActivity.description}>Log Activity</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CustomerDetailPage;
